<template>
  <BareFullScreenPopup
    ref="popup"
    title="Оберіть варіант оплати"
    :closeable="true"
    :lock-scroll="true"
    @visibility-change="onVisibilityChange"
  >
    <div class="pay-option-selector">
      <div v-if="isPaymentSystemsLoading" class="pay-option-selector__loader">
        <SpinnerLoader
          :scale="2"
        />
      </div>

      <template v-else>
        <template v-for="option in paymentOptions">
          <button
            v-if="option.type === 'portmone'"
            :key="option.key"
            type="button"
            class="pay-option-selector__portmone"
            @click="option.handler"
          >
            <span class="pay-option-selector__portmone-main">
              <span class="pay-option-selector__portmone-title">{{ option.title }}</span>
              <span v-if="option.notice" class="pay-option-selector__portmone-notice">
                {{ option.notice }}
              </span>
            </span>
            <span class="pay-option-selector__portmone-aside">
              <span class="pay-option-selector__portmone-badge">{{ option.badge }}</span>
              <span class="pay-option-selector__portmone-chevron">›</span>
            </span>
          </button>

          <WhiteButton
            v-else
            :key="option.key"
            class="pay-option-selector__option"
            @click="option.handler"
          >
            <span class="pay-option-selector__content">
              <LiqPayLogoIcon v-if="option.icon === 'liqpay'" class="pay-option-selector__icon" />
              <span class="pay-option-selector__title">
                {{ option.title }}
                <span v-if="option.notice" class="pay-option-selector__notice">
                  {{ option.notice }}
                </span>
              </span>
            </span>
          </WhiteButton>
        </template>
      </template>
    </div>
  </BareFullScreenPopup>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { Pluralizer } from '@/shared/lib/string';
import { WhiteButton } from '@/shared/ui/form';
import { LiqPayLogoIcon } from '@/shared/ui/icons';
import { SpinnerLoader } from '@/shared/ui/loader';
import { BareFullScreenPopup } from '@/shared/ui/popup';

const { getPluralText } = Pluralizer();
const PORTMONE_MIN_AMOUNT = 500;

export default {
  name: 'PaymentSystemSelector',
  components: { BareFullScreenPopup, WhiteButton, LiqPayLogoIcon, SpinnerLoader },
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
    programName: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      paySum: 0,
      earningsSum: 0,
      orderData: null,
      unavailablePaymentWays: [],
    };
  },
  created() {
    this.$eventBus.$on('payment-way-unavailable', this.markPaymentWayUnavailable);
  },
  beforeDestroy() {
    this.$eventBus.$off('payment-way-unavailable', this.markPaymentWayUnavailable);
  },
  computed: {
    ...mapGetters('currentUserModule', ['getUserInfo']),
    ...mapGetters('paymentSystemsModule', ['getPaymentSystems', 'isPaymentSystemsLoading']),
    isOrganizationUser() {
      return !!this.getUserInfo.organizationId;
    },
    portmonePaymentSystem() {
      return this.getPaymentSystems.find(p => p.code === 'portmone');
    },
    portmoneInstallmentTerms() {
      return this.portmonePaymentSystem?.partial_pay_periods ?? [];
    },
    paymentOptions() {
      const options = [];

      ['client', 'agent', 'uinsure'].forEach(paidBy => {
        if (this.isLiqPayAvailable(paidBy)) {
          options.push({
            key: `liqpay_${paidBy}`,
            icon: 'liqpay',
            title: 'Рахунок на оплату LiqPay',
            notice: this.isAcquireCommissionMayBePaid('liqpay', paidBy)
              ? (paidBy === 'uinsure'
                ? 'Комісія не стягується'
                : `Комісію ${this.getAcquireCommission('liqpay')}% оплачує ${this.isAcquireCommissionPayAgent(paidBy) ? 'агент' : 'клієнт'}`)
              : null,
            handler: () => this.onSelectPaymentSystem('liqpay', paidBy),
          });
        }

        if (this.isiPayAvailable(paidBy)) {
          options.push({
            key: `ipay_${paidBy}`,
            icon: null,
            title: 'Оплатити через iPay',
            notice: this.isAcquireCommissionMayBePaid('ipay', paidBy)
              ? (paidBy === 'uinsure'
                ? 'Комісія не стягується'
                : this.isAcquireCommissionPayAgent(paidBy)
                  ? `Комісію ${this.getAcquireCommission('ipay')}% оплачує агент`
                  : 'Комісію за еквайринг оплачує клієнт')
              : null,
            handler: () => this.onSelectPaymentSystem('ipay', paidBy),
          });
        }

        if (this.isHostPayAvailable(paidBy)) {
          options.push({
            key: `hostpay_${paidBy}`,
            icon: null,
            title: 'Оплатити через HostPay',
            notice: this.isAcquireCommissionMayBePaid('hostpay', paidBy)
              ? (paidBy === 'uinsure'
                ? 'Комісія не стягується'
                : this.isAcquireCommissionPayAgent(paidBy)
                  ? `Комісію ${this.getAcquireCommission('hostpay')}% оплачує агент`
                  : 'Комісію за еквайринг оплачує клієнт')
              : null,
            handler: () => this.onSelectPaymentSystem('hostpay', paidBy),
          });
        }

        this.portmoneInstallmentTerms.forEach(term => {
          if (!this.isPortmoneAvailable(term, paidBy)) {
            return;
          }

          options.push({
            key: `portmone_${term}_${paidBy}`,
            type: 'portmone',
            title: 'Розстрочка Portmone',
            badge: this.getTermMonthsLabel(term),
            notice: paidBy === 'uinsure'
              ? 'Комісія не стягується'
              : `Комісію ${this.getPortmoneTermCommission(term)}% оплачує ${this.isAcquireCommissionPayAgent(paidBy) ? 'агент' : 'клієнт'}`,
            handler: () => this.onSelectPortmone(term, paidBy),
          });
        });
      });

      options.push({
        key: 'legal-invoice',
        icon: null,
        title: 'Рахунок-фактура',
        notice: null,
        handler: this.onSelectLegalInvoice,
      });

      return options;
    },
  },
  watch: {
    companyId: {
      immediate: false,
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.loadPaymentSystems();
        }
      },
    },
  },
  methods: {
    ...mapActions('paymentSystemsModule', ['fetchPaymentSystemsList']),
    open({ price, earnings, orderData }) {
      this.paySum = price;
      this.earningsSum = earnings;
      this.orderData = orderData;

      this.loadPaymentSystems();

      this.$nextTick(() => {
        this.$refs.popup.open();
      });
    },
    close() {
      this.$refs.popup?.close();
    },
    onVisibilityChange({ isVisible }) {
      if (!isVisible) {
        this.$emit('closed');
      }
    },
    loadPaymentSystems() {
      if (!this.companyId) return;

      this.fetchPaymentSystemsList({
        companyId: this.companyId,
        programName: this.programName,
      });
    },
    isPaymentSystemEnabled(provider, paidBy) {
      const item = this.getPaymentSystems.find(p => p.code === provider);
      if (!item?.is_enabled) {
        return false;
      }

      const options = item.available_options ?? [];
      if (paidBy !== 'uinsure' && options.includes('uinsure')) {
        return false;
      }

      return options.includes(paidBy);
    },
    getAcquireCommission(provider) {
      const item = this.getPaymentSystems.find(p => p.code === provider);
      return item?.percentage_acquiring ?? 0;
    },
    isAcquireCommissionPayAgent(paidBy) {
      return paidBy === 'agent';
    },
    isAcquireCommissionMayBePaid(provider, paidBy) {
      if (!this.isAcquireCommissionPayAgent(paidBy)) {
        return true;
      }

      const commissionPercent = this.getAcquireCommission(provider);
      if (commissionPercent <= 0) {
        return false;
      }

      const commissionSum = this.paySum * commissionPercent / 100;
      return this.earningsSum >= commissionSum;
    },
    isLiqPayAvailable(paidBy) {
      return this.isPaymentSystemEnabled('liqpay', paidBy) && !this.isOrganizationUser && this.isAcquireCommissionMayBePaid('liqpay', paidBy);
    },
    isiPayAvailable(paidBy) {
      return this.isPaymentSystemEnabled('ipay', paidBy) && !this.isOrganizationUser && this.isAcquireCommissionMayBePaid('ipay', paidBy);
    },
    isHostPayAvailable(paidBy) {
      return this.isPaymentSystemEnabled('hostpay', paidBy) && !this.isOrganizationUser && this.isAcquireCommissionMayBePaid('hostpay', paidBy);
    },
    getTermMonthsLabel(term) {
      return getPluralText(term, ['місяць', 'місяці', 'місяців']);
    },
    getPortmoneTermCommission(term) {
      const ps = this.portmonePaymentSystem;
      const termData = (ps?.installment_terms ?? [])
        .find(item => Number(item.term_months) === Number(term));

      return Number(termData?.percentage_acquiring ?? ps?.percentage_acquiring ?? 0);
    },
    isPortmoneCommissionMayBePaid(term, paidBy) {
      if (!this.isAcquireCommissionPayAgent(paidBy)) {
        return true;
      }

      const commissionPercent = this.getPortmoneTermCommission(term);
      if (commissionPercent <= 0) {
        return false;
      }

      return this.earningsSum >= this.paySum * commissionPercent / 100;
    },
    isPortmoneAvailable(term, paidBy) {
      return this.isPaymentSystemEnabled('portmone', paidBy)
        && !this.isOrganizationUser
        && this.paySum >= PORTMONE_MIN_AMOUNT
        && this.isPortmoneCommissionMayBePaid(term, paidBy)
        && !this.isPaymentWayUnavailable('portmone', paidBy, term);
    },
    markPaymentWayUnavailable({ code, paidBy, term = null }) {
      if (!code) {
        return;
      }

      const exists = this.unavailablePaymentWays.some(item =>
        item.code === code
        && item.paidBy === paidBy
        && (item.term ?? null) === (term ?? null)
      );

      if (!exists) {
        this.unavailablePaymentWays.push({ code, paidBy, term: term ?? null });
      }
    },
    isPaymentWayUnavailable(code, paidBy, term = null) {
      return this.unavailablePaymentWays.some(item =>
        item.code === code
        && item.paidBy === paidBy
        && (item.term ?? null) === (term ?? null)
      );
    },
    onSelectPortmone(term, paidBy) {
      const paymentSystem = this.portmonePaymentSystem;

      this.$emit('selectPaymentSystem', {
        orderData: this.orderData,
        providerData: {
          code: 'portmone',
          title: `${paymentSystem?.title ?? 'Portmone'} (розстрочка ${term} міс.)`,
          percentage_acquiring: this.getPortmoneTermCommission(term),
          paid_by: paidBy,
          invoiceType: 'portmone-installment',
          term,
        },
      });
      this.close();
    },
    onSelectPaymentSystem(provider, paidBy) {
      const paymentSystem = this.getPaymentSystems.find(p => p.code === provider);

      this.$emit('selectPaymentSystem', {
        orderData: this.orderData,
        providerData: {
          code: provider,
          title: paymentSystem?.title ?? '',
          percentage_acquiring: paymentSystem?.percentage_acquiring ?? 0,
          paid_by: paidBy,
        },
      });
      this.close();
    },
    onSelectLegalInvoice() {
      this.$emit('selectLegalInvoice', { orderData: this.orderData });
      this.close();
    },
  },
};
</script>

<style scoped lang="scss">
.pay-option-selector {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  margin-top: 24px;

  &__option {
    width: 100%;
    max-width: 400px;
    font-weight: 500;
    font-size: 14px;
    background-color: #F6F6F9;
    border: none;
    padding: 16px;

    @media (max-width: 600px) {
      font-size: 16px;
    }
  }

  &__portmone {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    width: 100%;
    max-width: 400px;
    padding: 18px 20px 18px 24px;
    border: none;
    border-radius: 5px;
    background: linear-gradient(180deg, #12A7EC 0%, #0A8FD0 100%);
    box-shadow: 0 6px 16px rgba(10, 143, 208, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.28);
    cursor: pointer;
    transition: transform 0.16s ease, box-shadow 0.16s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 10px 22px rgba(10, 143, 208, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3);
    }
  }

  &__portmone-main {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    text-align: left;
  }

  &__portmone-title {
    font-weight: 700;
    font-size: 18px;
    line-height: 1.2;
    color: #FFFFFF;
  }

  &__portmone-notice {
    font-weight: 600;
    font-size: 12px;
    line-height: 1.2;
    color: rgba(255, 255, 255, 0.9);
  }

  &__portmone-aside {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  &__portmone-badge {
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #0B6F9E;
    background: #FFFFFF;
    padding: 7px 11px;
    border-radius: 999px;
    white-space: nowrap;
  }

  &__portmone-chevron {
    font-weight: 700;
    font-size: 18px;
    line-height: 1;
    color: #FFFFFF;
    opacity: 0.85;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    width: 100%;
  }

  &__icon {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  &__title {
    display: flex;
    flex-direction: column;
    text-align: center;
  }

  &__notice {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    text-align: center;
    color: #009BEB;
  }

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
  }
}

::v-deep .fullscreen-popup__content-wrapper {
  max-width: 500px;
}
</style>
